import React, { useEffect, useState, useContext, Suspense } from 'react'
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom'
import { useIntercom } from 'react-use-intercom';
import { Slide, toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Helmet, HelmetProvider } from 'react-helmet-async'
import { differenceInSeconds } from 'date-fns';

import Home from '@/pages/home'
import Arena from '@/pages/arena'
import Settings from './pages/settings';
import SettingsGeneral from './pages/settings/general';
import SettingsSecurity from './pages/settings/security';
import SettingsPreferences from './pages/settings/preferences';
import SettingsIgnored from './pages/settings/ignored';
import SettingsOffers from './pages/settings/offers';
import Leaderboard from './pages/leaderboard';
import Account from './pages/account';
import AccountGeneral from './pages/account/general';
import AccountTransactions from './pages/account/transactions';
import AccountBets from './pages/account/bets';

import { AuthContext } from '@/contexts/AuthContext'
import { useFirestore, useFirestoreConnect } from "react-redux-firebase";
import { usePrevious } from '@/hooks/usePrevious'
import ProtectedRoute from './components/ProtectedRoute';
import Loader from './components/Loader'
import { collectionToArray } from '@/utils/collectionToArray'
import Toast from './components/Toast';
import { UserIcon } from '@heroicons/react/outline';
import Privacy from './pages/privacy';
import Tos from './pages/tos';
import { useSelector } from 'react-redux';
// import Dice from './pages/dice';

export default function App() {
  const router = useNavigate();
  const firestore = useFirestore()
  const { search } = useLocation();
  const { auth, user, userRef, userDuels, notifications } = useContext(AuthContext);
  const previousUserDuels = usePrevious(userDuels)
  const { boot } = useIntercom();

  React.useEffect(() => {
    if (user && user.username && auth?.emailVerified) {
      boot({
        customAttributes: {
          hide_default_launcher: true,
          custom_launcher_selector: '#custom-intercom',
          email: auth?.email,
          name: user?.username,
          user_id: auth?.uid
        },
      })
    }
  }, [user, auth])

  useFirestoreConnect([
    {
      collection: `users/${auth.uid}/notification`,
      storeAs: "notifications",
    },
    {
      collection: "users",
      doc: auth.uid,
      storeAs: "user",
    },
    {
      collection: `users/${auth.uid}/accounts`,
      storeAs: "userAccounts",
    },
    {
      collection: "duels",
      where: [
        ["createdUid", "==", `${auth.uid}`],
        ["completed", "==", false]
      ],
      storeAs: "userduels",
    },
    {
      collection: "duels",
      where: [
        ["joinedUid", "==", `${auth.uid}`],
        ["completed", "==", false]
      ],
      storeAs: "joinedduels",
    },
    {
      collection: "orders",
      where: [
        ["user", "==", userRef],
      ],
      storeAs: "userorders",
    },
    {
      collection: "duels",
      orderBy: ['fightEnd', 'desc'],
      storeAs: "recentDuels"
    },
    {
      collection: "settings",
      doc: "exchange_rate",
      storeAs: "exchangeRate"
    },
    {
      collection: "settings",
      doc: "fiat_rate",
      storeAs: "fiatRate"
    },
    {
      collection: "settings",
      doc: "tax_rate",
      storeAs: "taxRate"
    },
    {
      collection: "settings",
      doc: "maintenance",
      storeAs: "maintenance"
    },
    {
      collection: "races",
      where: [
        ["completed", "==", false]
      ],
      storeAs: "races"
    },
    {
      collection: 'stats',
      storeAs: 'stats'
    }
  ]);
  useFirestoreConnect([
    {
      collection: "duels",
      where: [
        ["hidden", "==", false]
      ],
      storeAs: "duels",
    },
    {
      collection: "messages",
      orderBy: ["timestamp", "desc"],
      storeAs: "messages",
    },
    {
      collection: "messages",
      orderBy: ["timestamp", "desc"],
      limit: 40,
      storeAs: "messageHistory",
    },
  ]);

  useEffect(() => {
    if (userDuels && previousUserDuels) {
      const stakeId = Object.keys(userDuels)[0]
      const flatPrevious = Object.values(previousUserDuels).flat()[0]
      const flatUserDuels = Object.values(userDuels).flat()[0]
      if (!flatPrevious.joined && flatUserDuels.joined) {
        router({ search: `?modal=startDuel&id=${stakeId}` })
      }
    }
  }, [userDuels])
  const [watchingNotifications, setWatchingNotifications] = useState(false)
  useEffect(() => {
    if (auth?.uid && !watchingNotifications) {
      setWatchingNotifications(true)
      firestore.collection(`users/${auth?.uid}/notification`).where("createdAt", ">=", firestore.Timestamp.fromDate(new Date())).onSnapshot(snapshot => {
        snapshot.docChanges().forEach(change => {
          if (change.type == "added") {
            toast(<Toast icon={<UserIcon className="h-6 w-6 text-gray-300" />} headline={change.doc.data().headline} text={change.doc.data().text} />)
          }
        })
      })
    }
  }, [auth])

  if (!auth.isLoaded) {
    return (
      <div className="min-h-screen fixed inset-0 z-[500] bg-gray-900 flex justify-center items-center">
        <div>
          <Loader />
        </div>
      </div>
    )
  }

  return (
    <>
      <Suspense fallback={<div className="min-h-screen flex justify-center items-center">
        <div>
          <Loader />
        </div>
      </div>}>
        <HelmetProvider>
          <Helmet titleTemplate="%s - Duels.gg">
            <html lang="en" />
            <meta name="description" content="Duels.gg is the ultimate runescape staking experience. Instantly deposit and withdraw 07, RS3, BTC, ETH, & more. No limit Runescape Duel Arena Staking." />
            <meta name="theme-color" content="#0f172a" />
          </Helmet>
          <ToastContainer
            position="top-left"
            autoClose={3000}
            pauseOnFocusLoss={false}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            draggable
            transition={Slide}
          />
          <Routes>
            <Route exact path="/" element={<Home />} />
            <Route path="arena" element={<Arena />} />
            <Route path="leaderboard" element={<Leaderboard />} />
            <Route path="tos" element={<Tos />} />
            <Route path="privacy" element={<Privacy />} />

            <Route element={<ProtectedRoute />}>
              <Route path="settings" element={<Settings />}>
                <Route path="general" element={<SettingsGeneral />} />
                <Route path="security" element={<SettingsSecurity />} />
                <Route path="preferences" element={<SettingsPreferences />} />
                <Route path="ignored" element={<SettingsIgnored />} />
                <Route path="offers" element={<SettingsOffers />} />
              </Route>
              <Route path="account" element={<Account />}>
                <Route path="general" element={<AccountGeneral />} />
                <Route path="transactions" element={<AccountTransactions />} />
                <Route path="bets" element={<AccountBets />} />
                {/* <Route path="security" element={<SettingsSecurity />} />
                <Route path="preferences" element={<SettingsPreferences />} />
                <Route path="ignored" element={<SettingsIgnored />} />
                <Route path="offers" element={<SettingsOffers />} /> */}
              </Route>
              {/* <Route path="dice" element={<Dice />} /> */}
            </Route>
          </Routes>
        </HelmetProvider>
      </Suspense>
    </>

  )
}